import React from 'react';

// Paleta con contraste ≥ 4.5:1 sobre la hoja marfil (#FBF8F0)
export const C = {
  tinta: '#2F4A33',
  carbon: '#1F2A1F',
  suave: '#424B3F',
  verde: '#3E5A41',
  oro: '#A8792E',
  oroTexto: '#7A5418',
  linea: 'rgba(62,90,65,0.35)',
  fondo: '#F3EFE3',
  superficie: '#FBF8F0',
};

export const retraso = (s) => ({ animationDelay: `${s}s` });

export const Eyebrow = ({ children, style, className = '' }) => (
  <p className={`boda-anim boda-sans text-[0.875rem] font-semibold uppercase ${className}`}
    style={{ color: C.verde, letterSpacing: '0.16em', ...style }}>
    {children}
  </p>
);

export const Divisor = ({ style }) => (
  <div className="boda-anim mx-auto flex w-full max-w-[240px] items-center gap-3" style={style} aria-hidden>
    <span className="h-px flex-1" style={{ background: C.linea }} />
    <svg width="34" height="16" viewBox="0 0 34 16" className="shrink-0">
      <path d="M17 2 C 20 6 24 7 30 8 C 24 9 20 10 17 14 C 14 10 10 9 4 8 C 10 7 14 6 17 2 Z" fill={C.verde} opacity="0.75" />
      <circle cx="17" cy="8" r="1.6" fill={C.oro} />
    </svg>
    <span className="h-px flex-1" style={{ background: C.linea }} />
  </div>
);

const IconoInstagram = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden className={className}>
    <rect x="2.5" y="2.5" width="19" height="19" rx="5" />
    <circle cx="12" cy="12" r="4.25" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

export const ColoresReservados = ({ colores, style }) => {
  const varios = colores.length > 1;
  return (
    <section className="boda-anim mt-12" style={style} data-testid="landing-colores">
      <Eyebrow>{varios ? 'Colores reservados' : 'Color reservado'}</Eyebrow>
      <div className="mt-6 flex flex-wrap items-start justify-center gap-7 sm:gap-11">
        {colores.map((c, i) => (
          <div key={`${c.nombre}-${c.hex}-${i}`} className="flex flex-col items-center gap-3">
            <span className="block h-16 w-16 rounded-full sm:h-20 sm:w-20" aria-hidden
              style={{ background: c.hex, boxShadow: `inset 0 2px 6px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.1), 0 0 0 5px ${C.superficie}, 0 0 0 6px ${C.linea}` }} />
            <span className="text-xl font-semibold sm:text-2xl" style={{ color: C.tinta }}>{c.nombre}</span>
          </div>
        ))}
      </div>
      <p className="boda-sans mx-auto mt-6 max-w-md text-base leading-relaxed" style={{ color: C.suave }}>
        {varios
          ? 'La pareja reservó estos colores para su celebración; como invitado, evítalos en tu atuendo.'
          : 'La pareja reservó este color para su celebración; como invitado, evítalo en tu atuendo.'}
      </p>
    </section>
  );
};

export const Redes = ({ redes = [], style }) => {
  const visibles = redes.filter(r => r.url);
  if (!visibles.length) return null;
  return (
    <section className="boda-anim mt-14" style={style} data-testid="landing-redes">
      <Divisor />
      <Eyebrow className="mt-9">Síguenos</Eyebrow>
      <div className="boda-sans mt-4 flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-8">
        {visibles.map(r => (
          <a key={r.url} href={r.url} target="_blank" rel="noopener noreferrer" className="boda-enlace inline-flex min-h-12 items-center gap-2.5 text-base">
            <IconoInstagram className="h-5 w-5 shrink-0" />
            <span>{r.usuario || 'Instagram'}{r.nombre && <span className="font-normal" style={{ color: C.suave }}> · {r.nombre}</span>}</span>
          </a>
        ))}
      </div>
    </section>
  );
};

export const PieMarca = ({ marca, style }) => (
  <section className="boda-anim mt-14 flex flex-col items-center" style={style}>
    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/brand/logo/image`} alt={marca} className="h-14 w-[210px] object-contain sm:h-16 sm:w-[240px]"
      style={{ filter: 'brightness(0)', opacity: 0.85 }} />
    <p className="boda-sans mt-3 text-sm font-semibold uppercase" style={{ color: C.oroTexto, letterSpacing: '0.2em' }}>Sastrería a medida</p>
  </section>
);

// Pequeño y en la esquina: nunca tapa campos ni botones gracias al espacio libre al final de la página
export const WhatsAppFlotante = ({ href }) => {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" aria-label="Escríbenos por WhatsApp" data-testid="landing-whatsapp-flotante"
      className="fixed bottom-3 right-3 z-[60] flex h-11 w-11 items-center justify-center rounded-full text-white shadow-[0_6px_18px_rgba(0,0,0,0.28)] ring-2 ring-white/80 transition-transform hover:scale-105 md:bottom-6 md:right-6 md:h-14 md:w-14"
      style={{ background: C.tinta }}>
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="h-5 w-5 md:h-6 md:w-6">
        <path d="M17.472 14.382c-.297-.149-1.758-.868-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
        <path d="M12.041 2C6.58 2 2.13 6.45 2.13 11.911c0 1.891.525 3.66 1.44 5.176L2 22l5.041-1.523a9.87 9.87 0 0 0 5 1.357c5.462 0 9.912-4.451 9.912-9.923C21.953 6.45 17.503 2 12.041 2zm0 18.083a8.14 8.14 0 0 1-4.148-1.134l-.297-.174-3.007.908.916-2.939-.198-.297a8.13 8.13 0 0 1-1.244-4.34c0-4.497 3.66-8.157 8.156-8.157 4.478 0 8.174 3.678 8.174 8.157 0 4.497-3.678 8.157-8.174 8.157z" />
      </svg>
    </a>
  );
};
